import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import translations, { LANGUAGES } from "./translations";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    const saved = localStorage.getItem("lang");
    const browser = navigator.language?.slice(0, 2);
    const supported = LANGUAGES.map(l => l.code);
    return supported.includes(saved) ? saved
         : supported.includes(browser) ? browser
         : "fr";
  });

  // ── Au montage : appliquer dir + lang sur <html> ──
  useEffect(() => {
    const langObj = LANGUAGES.find(l => l.code === lang);
    document.documentElement.dir = langObj?.dir || "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  // ── Charger la préférence depuis le backend si connecté ──
  useEffect(() => {
    const token = localStorage.getItem("access");
    if (!token) return;  // pas connecté → on garde le localStorage / browser

    // Import dynamique pour éviter une dépendance circulaire avec accountClient
    import("./accountClient")
      .then(({ getProfile }) => getProfile())
      .then(r => {
        const serverLang = r?.data?.language;
        if (serverLang && LANGUAGES.find(l => l.code === serverLang)) {
          // Si le backend a une préférence DIFFÉRENTE du localStorage,
          // on synchronise (le backend fait foi)
          const local = localStorage.getItem("lang");
          if (serverLang !== local) {
            setLangState(serverLang);
            localStorage.setItem("lang", serverLang);
          }
        }
      })
      .catch(() => {
        // Erreur réseau / token expiré → ignore, on garde le localStorage
      });
  }, []);   // une seule fois au montage

  const setLang = useCallback((code) => {
    if (!LANGUAGES.find(l => l.code === code)) return;   // garde-fou

    setLangState(code);
    localStorage.setItem("lang", code);

    // Set dir attribute for RTL support
    const langObj = LANGUAGES.find(l => l.code === code);
    document.documentElement.dir = langObj?.dir || "ltr";
    document.documentElement.lang = code;

    // Persister côté backend (silencieux, non bloquant)
    const token = localStorage.getItem("access");
    if (token) {
      import("./accountClient")
        .then(({ updateProfile }) => updateProfile({ language: code }))
        .catch(err => {
          // En cas d'échec serveur, on garde la pref locale
          console.warn("Failed to save language to backend:", err);
        });
    }
  }, []);

  const t = useCallback((key) => {
    const entry = translations[key];
    if (!entry) return key;
    return entry[lang] ?? entry["fr"] ?? key;
  }, [lang]);

  const tc = useCallback((key) => {
    const raw = t(key);
    if (!raw.includes("<span>")) return raw;
    const parts = raw.split(/(<span>.*?<\/span>)/g);
    return parts.map((p, i) => {
      const m = p.match(/^<span>(.*)<\/span>$/);
      return m
        ? <span key={i} style={{ color: "inherit" }} className="i18n-highlight">{m[1]}</span>
        : p;
    });
  }, [t]);

  const currentLang = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

  return (
    <I18nContext.Provider value={{ lang, setLang, t, tc, currentLang, languages: LANGUAGES }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}



export { LANGUAGES };